import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Alert, Button, Modal, message } from 'antd';
import { isEmpty } from 'lodash';
import './index.less';
import AddQuickEntriesDrawer from './components/AddQuickEntriesDrawer';
import QuickEntrySortableRow from './QuickEntrySortableRow';
import { arrayMoveImmutable } from 'array-move';
import { quickEntriesListApi, quickEntriesStatusApi, quickEntriesSortApi, quickEntriesDeleteApi } from '@services/MedProject';
import PageHeader from '@components/PageHeader/PageHeader';
import { useProjectContext } from '@/contexts/ProjectContext';
import { WORKSPACE_PARENT_PATH, WORKSPACE_ROUTE_BASE } from '@/layouts/workspaceConfig';
import useSortDragUserSelectChrome from '../../hooks/useSortDragUserSelectChrome';

export default function RelatedProjectList() {
    const params = useParams();
    const { id: projectContextId, name: projectName } = useProjectContext();

    const projectId = (params?.projectId ?? params?.id) || 0; // 项目 ID

    useEffect(() => {
        document.title = '快捷入口-医项目';
    }, []);

    // 快捷入口列表数据
    const [quickEntriesList, setQuickEntriesList] = useState([]);
    // 新增快捷入口 Drawer
    const [visibleAddQuickEntriesDrawer, setVisibleAddQuickEntriesDrawer] = useState(false);
    // 当前选中的快捷入口
    const [currentQuickEntries, setCurrentQuickEntries] = useState(false);

    const dndSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        }),
    );

    const { beginSortDrag, endSortDrag } = useSortDragUserSelectChrome();

    // 快捷入口刪除
    const deleteQuickEntries = id => {
        Modal.confirm({
            title: '提示',
            content: '是否确认删除当前快捷入口',
            cancelText: '取消',
            okText: '确认',
            closable: true,
            onOk: async () => {
                const res = await quickEntriesDeleteApi({ id });
                if (res.status_code === 200) {
                    message.success('删除成功');
                    getQuickEntries();
                }
            },
        });
    };

    // 显示/隐藏
    const quickEntriesSwitchChange = async (checked, id) => {
        // console.log(checked, id);
        const reqParams = {
            hidden: checked ? 0 : 1,
            id: id,
        };
        const res = await quickEntriesStatusApi(reqParams);
        if (res.status_code === 200) {
            // console.log(res.data);
            getQuickEntries();
        }
    };

    const showAddQuickEntriesDrawer = () => {
        setCurrentQuickEntries(null);
        setVisibleAddQuickEntriesDrawer(true);
    };

    const showEditQuickEntriesDrawer = module => {
        if (!isEmpty(module)) {
            console.log(module);
            setCurrentQuickEntries(module);

            setVisibleAddQuickEntriesDrawer(true);
        }
    };

    // 快捷入口排序（@dnd-kit）
    const onDragEndQuickEntries = useCallback(
        event => {
            endSortDrag();
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const oldIndex = quickEntriesList.findIndex(item => item.id === active.id);
            const newIndex = quickEntriesList.findIndex(item => item.id === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            const newData = arrayMoveImmutable(quickEntriesList, oldIndex, newIndex);
            setQuickEntriesList(newData);
            quickEntriesSortApi(newData.map(item => item.id));
        },
        [quickEntriesList, endSortDrag],
    );

    // 获取项目的快捷入口
    const getQuickEntries = async () => {
        const res = await quickEntriesListApi({ projectId });
        if (res.status_code === 200) {
            setQuickEntriesList(res.data);
        }
    };

    useEffect(() => {
        if (!!projectId) {
            getQuickEntries();
        }
    }, [projectId]);

    return (
        <>
            <PageHeader title="快捷入口" breadcrumb={[{ label: '医项目', path: WORKSPACE_PARENT_PATH }, { label: projectName, path: `${WORKSPACE_ROUTE_BASE}/${projectContextId}` }, '快捷入口']} />

            <div className="page-route-body">
                <div className="main-tools">
                    <Button type="primary" onClick={() => showAddQuickEntriesDrawer()}>
                        添加快捷入口
                    </Button>
                </div>

                <Alert style={{ marginBottom: 26, marginTop: 16 }} type="warning" title="该处功能类似金刚位，可以添加如联系我们，加入社群等快捷入口，该处添加的内容，将展示在主页右侧靠上位置" />

                <div className="main-table">
                    <div className="main-table-head">
                        <span className="main-table-name">快捷入口名称</span>
                        <span className="main-table-blank">入口跳转类型</span>
                        <span className="main-table-onoff">显示/隐藏</span>
                        <span className="main-table-setting">操作</span>
                    </div>
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragStart={beginSortDrag} onDragCancel={endSortDrag} onDragEnd={onDragEndQuickEntries}>
                        <SortableContext items={quickEntriesList.map(m => m.id)} strategy={verticalListSortingStrategy}>
                            <div className="main-table-body">
                                {quickEntriesList.map(module => (
                                    <QuickEntrySortableRow key={module.id} module={module} quickEntriesSwitchChange={quickEntriesSwitchChange} showEditQuickEntriesDrawer={showEditQuickEntriesDrawer} deleteQuickEntries={deleteQuickEntries} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>

            <AddQuickEntriesDrawer visibleAddQuickEntriesDrawer={visibleAddQuickEntriesDrawer} setVisibleAddQuickEntriesDrawer={setVisibleAddQuickEntriesDrawer} currentQuickEntries={currentQuickEntries} getQuickEntries={getQuickEntries} />
        </>
    );
}
